/**
 * StructurePickerPanel - pick or create a structure on an existing property.
 *
 * When a registration is started against a property host that already
 * exists, that property may carry multiple structures (e.g. main house,
 * garage, "Other / Apartments"). This panel lets the operator either
 * SELECT one of the property's existing structures or CREATE a new one
 * inline, without leaving the wizard.
 *
 * Carry-through: the selected/created structure's attribute values are
 * merged into ``state.hostAttributeValues``. The backend resolves the
 * persisted structure for the registration from this host payload via
 * ``get_or_create_structure_from_registration`` (identity-matched on
 * ``structure_type`` / ``other_structure_type`` / address), so the new
 * registration attaches to the right structure with no extra payload
 * field required.
 *
 * Everything is data-driven: the structure schema (its attributes,
 * labels, options) comes from the ``hosts.Structure`` EntityType in the
 * registry - the same source the property hub's "Add structure" dialog
 * uses - never a hardcoded field list.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Boxes, Check, Pencil, Plus, Trash2 } from 'lucide-react'
import api from '@/admin/lib/api'
import { toast } from 'sonner'
import { toastApiError } from '@/shared/lib/api-error'
import {
  useEntityTypes,
  type EntityAttributeDefinition,
} from '@/admin/hooks/use-entities'
import { canEntityPermission } from '@/admin/lib/entity-permissions'
import { usePermissions } from '@/admin/hooks/use-permissions'
import { EditFormDialog } from '@/admin/components/shared/edit-form-dialog'
import { ConfirmDeleteDialog } from '@/admin/components/confirm-delete-dialog'
import {
  defineField,
  type FieldType,
  type FormFieldMeta,
} from '@/shared/lib/forms'
import { Button } from '@/shared/components/ui/button'
import { LucideIconByName } from '@/shared/components/ui/lucide-icon-by-name'
import { cn } from '@/shared/lib/utils'
import type { StructureSummary } from '@/admin/types/hosts'
import type { WizardState } from '@/admin/pages/registrations/new-registration-page'
import { RegistrationPanel } from './layout'

// ---------------------------------------------------------------------------
// Display helpers (kept tenant-agnostic - all copy is derived from data)
// ---------------------------------------------------------------------------

function titleCase(value: unknown): string {
  const label = String(value ?? '').trim()
  if (!label) return ''
  // Normalise snake_case / kebab-case codes (e.g. "not_listed",
  // "single-family") into spaced words first, then title-case, so raw
  // identifiers never reach the user as "Not_listed".
  return label
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
    .replace(/\b\p{L}/gu, (char) => char.toUpperCase())
}

function isOtherStructureValue(value: unknown): boolean {
  return String(value ?? '').trim().toLowerCase() === 'other'
}

/**
 * A structure is "unspecified" when it is the generic catch-all bucket -
 * an `Other` type whose detail is blank or a not-listed sentinel. This is
 * tenant-agnostic: it recognises the *concept* of an unspecified
 * structure, not any one tenant's value. Used to sink the fallback to the
 * bottom of the list and to never auto-select it.
 */
function isUnspecifiedStructure(structure: StructureSummary): boolean {
  const values = structure.attribute_values ?? {}
  if (!isOtherStructureValue(values.structure_type)) return false
  const detail = String(values.other_structure_type ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
  return (
    detail === '' ||
    detail === 'not listed' ||
    detail === 'unspecified' ||
    detail === 'none'
  )
}

/**
 * Ordering rank for the picker list: concrete named types first (0),
 * `Other` structures that still carry a real detail next (1), and the
 * unspecified "not listed" fallback last (2). `Array.sort` is stable, so
 * structures within a rank keep their original (backend) order.
 */
function structureSortRank(structure: StructureSummary): number {
  if (isUnspecifiedStructure(structure)) return 2
  if (isOtherStructureValue(structure.attribute_values?.structure_type)) {
    return 1
  }
  return 0
}

/** Human-friendly name + secondary line for a structure card. */
function structureDisplay(structure: StructureSummary): {
  name: string
  meta: string
} {
  const values = structure.attribute_values ?? {}
  const isOther = isOtherStructureValue(values.structure_type)
  const otherType = isOther ? titleCase(values.other_structure_type) : ''
  // Prefer the explicit label / structure_name over the other_structure_type
  // detail, so "Abbey Home" (label) wins over "Commercial" (other type).
  // Skip the label when it is just "Other" (matches the type, not a real
  // name) so the more descriptive other_structure_type detail wins.
  const labelName = titleCase(structure.label)
  const name =
    (isOther && labelName.toLowerCase() === 'other' ? '' : labelName) ||
    titleCase(values.structure_name) ||
    titleCase(values.name) ||
    otherType ||
    'Main structure'
  // Secondary line: for "Other" types show the detail (e.g. "Commercial");
  // for named types show the structure_type. Never echo the name back.
  const typeLabel = isOther ? otherType : titleCase(values.structure_type)
  const meta =
    typeLabel && typeLabel.toLowerCase() !== name.toLowerCase() ? typeLabel : ''
  return { name, meta }
}

function structureEntityAttributeToField(
  attr: EntityAttributeDefinition,
): FormFieldMeta {
  return defineField({
    code_name: attr.code_name,
    display_name: attr.display_name,
    field_type: attr.field_type as FieldType,
    required: attr.required,
    hint: attr.hint || undefined,
    description: attr.description || undefined,
    options: attr.configuration_data.options,
    order: attr.order,
    configuration_data: attr.configuration_data,
    source_uuid: attr.uuid,
  })
}

// ---------------------------------------------------------------------------
// Selectable card
// ---------------------------------------------------------------------------

function StructureCard({
  name,
  meta,
  selected,
  onClick,
  iconName,
  onEdit,
  onDelete,
}: {
  name: string
  meta: string
  selected: boolean
  onClick: () => void
  /**
   * Lucide icon name supplied by the structure type's config (via the
   * icon-name allowlist). Falls back to the generic Boxes glyph when the
   * type defines no icon.
   */
  iconName?: string | null
  /**
   * When provided, an edit / delete affordance is shown. Only supplied for
   * structures the operator added during this registration session (they are
   * kept as siblings of the selectable card, never nested buttons).
   */
  onEdit?: () => void
  onDelete?: () => void
}) {
  const showActions = !!onEdit || !!onDelete
  return (
    <div className="flex items-stretch gap-1.5">
      <Button
        type="button"
        variant="ghost"
        onClick={onClick}
        aria-pressed={selected}
        className={cn(
          'group flex h-auto min-w-0 flex-1 items-center justify-start gap-3 rounded-[10px] border-[0.5px] px-3.5 py-3 text-left transition-colors duration-150',
          selected
            ? 'border-[var(--green-border)] bg-[var(--green-bg)] hover:bg-[var(--green-bg)]'
            : 'border-[var(--rule)] bg-[var(--bg)] hover:border-[var(--green-border)] hover:bg-[var(--bg-subtle)]',
        )}
      >
        <span
          className={cn(
            'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors duration-150',
            selected
              ? 'border-[0.5px] border-[var(--green-border)] bg-[var(--bg)] text-[var(--green)]'
              : 'bg-[var(--bg-subtle)] text-[var(--ink-3)] group-hover:text-[var(--ink-2)]',
          )}
        >
          <LucideIconByName
            name={iconName}
            size={15}
            className="text-inherit"
            fallback={<Boxes size={15} className="text-inherit" />}
          />
        </span>
        <span className="min-w-0 flex-1">
          <span
            className={cn(
              'block truncate text-[13px]',
              selected
                ? 'font-medium text-[var(--ink)]'
                : 'text-[var(--ink)] group-hover:text-[var(--ink)]',
            )}
          >
            {name}
          </span>
          {meta ? (
            <span className="mt-0.5 block truncate text-[11px] font-light text-[var(--ink-3)]">
              {meta}
            </span>
          ) : null}
        </span>
        {selected ? (
          <Check
            size={16}
            className="shrink-0 text-[var(--green)]"
            strokeWidth={2.25}
          />
        ) : null}
      </Button>
      {showActions ? (
        <div className="flex shrink-0 items-center gap-1">
          {onEdit ? (
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              onClick={onEdit}
              aria-label={`Edit ${name}`}
              className="text-[var(--ink-3)] hover:text-[var(--ink)]"
            >
              <Pencil size={14} />
            </Button>
          ) : null}
          {onDelete ? (
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              onClick={onDelete}
              aria-label={`Delete ${name}`}
              className="text-[var(--ink-3)] hover:text-[var(--status-error)]"
            >
              <Trash2 size={14} />
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

function CreateStructureCard({
  selected,
  onClick,
}: {
  selected: boolean
  onClick: () => void
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        'group flex h-auto w-full items-center justify-start gap-3 rounded-[10px] border-[0.5px] border-dashed px-3.5 py-3 text-left transition-colors duration-150',
        selected
          ? 'border-[var(--green-border)] bg-[var(--green-bg)] hover:bg-[var(--green-bg)]'
          : 'border-[var(--rule-strong)] bg-[var(--bg)] hover:border-[var(--green-border)] hover:bg-[var(--bg-subtle)]',
      )}
    >
      <span
        className={cn(
          'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border-[0.5px] border-dashed transition-colors duration-150',
          selected
            ? 'border-[var(--green-border)] bg-[var(--bg)] text-[var(--green)]'
            : 'border-[var(--rule-strong)] bg-transparent text-[var(--ink-3)] group-hover:text-[var(--green)]',
        )}
      >
        <Plus size={15} className="text-inherit" />
      </span>
      <span
        className={cn(
          'font-display text-[13px] transition-colors duration-150',
          selected
            ? 'text-[var(--ink)]'
            : 'text-[var(--ink-3)] group-hover:text-[var(--ink-2)]',
        )}
      >
        Add a new structure
      </span>
    </Button>
  )
}

// ---------------------------------------------------------------------------
// Panel
// ---------------------------------------------------------------------------

/**
 * Reported up to the host step so it can hand structure-decision control
 * to this picker: when `active` is true the host step hides and stops
 * requiring the standalone structure fields (e.g. `structure_type`,
 * `other_structure_type`) named in `ownedCodeNames`, since selecting from
 * the picker IS the structure decision.
 */
export interface StructurePickerActivation {
  active: boolean
  ownedCodeNames: string[]
}

export function StructurePickerPanel({
  state,
  update,
  onActivationChange,
}: {
  state: WizardState
  update: (payload: Partial<WizardState>) => void
  /**
   * Notifies the host step whether this picker is in control of the
   * structure decision and which host code_names it owns, so the host
   * step can hide / stop requiring the redundant standalone fields.
   */
  onActivationChange?: (activation: StructurePickerActivation) => void
}) {
  const propertyHostUuid = state.selectedExistingHostUuid
  const { t } = useTranslation()
  const { can } = usePermissions()
  const queryClient = useQueryClient()
  const { data: entityTypes = [] } = useEntityTypes()

  const structureEntity = useMemo(
    () =>
      entityTypes.find(
        (entity) => String(entity.metadata?.storage ?? '') === 'hosts.Structure',
      ),
    [entityTypes],
  )

  const canViewStructures =
    !!structureEntity && canEntityPermission(can, structureEntity.key, 'view')
  const canCreateStructure =
    !!structureEntity && canEntityPermission(can, structureEntity.key, 'create')
  const canEditStructure =
    !!structureEntity && canEntityPermission(can, structureEntity.key, 'edit')
  const canDeleteStructure =
    !!structureEntity && canEntityPermission(can, structureEntity.key, 'delete')

  // Only meaningful for a property host that already exists. ``'new'`` and
  // ``null`` both mean "registering a brand-new property" - there are no
  // existing structures to choose from yet, so the host form itself
  // captures the first structure's details.
  const isExistingProperty =
    !!propertyHostUuid && propertyHostUuid !== 'new'

  const {
    data: structures = [],
    isLoading,
  } = useQuery<StructureSummary[]>({
    queryKey: ['host-structures', propertyHostUuid],
    queryFn: () =>
      api
        .get<StructureSummary[]>(`/hosts/${propertyHostUuid}/structures/`)
        .then((r) => r.data ?? []),
    enabled: isExistingProperty && canViewStructures,
  })

  const structureFields = useMemo(
    () =>
      (structureEntity?.attributes ?? [])
        .filter(
          (attr) =>
            attr.source !== 'COMPUTED' &&
            attr.editable &&
            !attr.code_name.startsWith('address_'),
        )
        .sort((a, b) => a.order - b.order)
        .map(structureEntityAttributeToField),
    [structureEntity?.attributes],
  )

  // The structure code_names that participate in the host payload. Used to
  // (a) merge a chosen structure's values into hostAttributeValues and
  // (b) detect which existing structure (if any) is currently selected by
  // comparing those keys against the current host values.
  const structureCodeNames = useMemo(
    () => structureFields.map((field) => field.code_name),
    [structureFields],
  )

  const [createOpen, setCreateOpen] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  // UUID of a structure created inline during this wizard session, so the
  // "Add a new structure" card stays highlighted after creation.
  const [createdStructureUuid, setCreatedStructureUuid] = useState<
    string | null
  >(null)
  // Every structure the operator added inline during THIS wizard session.
  // Edit / delete affordances are shown only for these - a property's
  // pre-existing structures are shared data and are never mutated from the
  // registration flow.
  const [sessionCreatedUuids, setSessionCreatedUuids] = useState<string[]>([])
  // The structure currently being edited (opens the edit dialog), and the
  // structure queued for deletion (opens the confirm dialog).
  const [editStructure, setEditStructure] = useState<StructureSummary | null>(
    null,
  )
  const [isEditing, setIsEditing] = useState(false)
  const [deleteStructure, setDeleteStructure] =
    useState<StructureSummary | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  // Per-type icons come from config, not code: each structure_type option
  // may declare an `icon` (a lucide name) in its configuration_data. We
  // map value -> icon name here and render it through the icon-name
  // allowlist, so a tenant can give Residential a house, Commercial a
  // building, etc. without any frontend change.
  const structureTypeIconByValue = useMemo(() => {
    const map: Record<string, string> = {}
    const options = structureFields.find(
      (field) => field.code_name === 'structure_type',
    )?.options
    for (const option of options ?? []) {
      if (!option || typeof option !== 'object') continue
      const icon = (option as { icon?: unknown }).icon
      const value = (option as { value?: unknown }).value
      if (typeof icon === 'string' && icon.trim()) {
        map[String(value ?? '')] = icon.trim()
      }
    }
    return map
  }, [structureFields])

  // Lead with concrete named structures; the unspecified "not listed"
  // fallback sinks to the bottom (just above "Add a new structure"). The
  // sort is stable so backend order is preserved within each rank.
  const orderedStructures = useMemo(
    () =>
      [...structures].sort(
        (a, b) => structureSortRank(a) - structureSortRank(b),
      ),
    [structures],
  )

  const hostValues = useMemo(
    () => state.hostAttributeValues ?? {},
    [state.hostAttributeValues],
  )

  // Resolve which structure card is currently active by matching the
  // structure-owned host values against each candidate's attribute values.
  const selectedStructureUuid = useMemo(() => {
    if (createdStructureUuid) return createdStructureUuid
    for (const structure of structures) {
      const values = structure.attribute_values ?? {}
      const matches = structureCodeNames.every((code) => {
        const candidate = values[code]
        const current = hostValues[code]
        const candidateEmpty =
          candidate === undefined || candidate === null || candidate === ''
        const currentEmpty =
          current === undefined || current === null || current === ''
        if (candidateEmpty && currentEmpty) return true
        return String(candidate ?? '') === String(current ?? '')
      })
      if (matches) return structure.uuid
    }
    return null
  }, [createdStructureUuid, hostValues, structureCodeNames, structures])

  const applyStructureValues = useCallback(
    (values: Record<string, unknown>) => {
      const next = { ...(state.hostAttributeValues ?? {}) }
      // Replace every structure-owned key so switching structures never
      // leaves stale values (e.g. a previous structure_type) behind.
      for (const code of structureCodeNames) {
        delete next[code]
      }
      for (const [code, value] of Object.entries(values)) {
        if (structureCodeNames.includes(code)) next[code] = value
      }
      update({ hostAttributeValues: next })
    },
    [state.hostAttributeValues, structureCodeNames, update],
  )

  const handleSelectExisting = useCallback(
    (structure: StructureSummary) => {
      setCreatedStructureUuid(null)
      applyStructureValues(structure.attribute_values ?? {})
    },
    [applyStructureValues],
  )

  const createInitialValues = useMemo(() => {
    const values: Record<string, unknown> = {}
    for (const code of structureCodeNames) {
      const current = hostValues[code]
      if (current !== undefined && current !== null && current !== '') {
        values[code] = current
      }
    }
    return values
  }, [hostValues, structureCodeNames])

  const handleCreate = useCallback(
    async (values: Record<string, unknown>) => {
      if (!propertyHostUuid) return
      setIsCreating(true)
      try {
        const created = await api
          .post<StructureSummary>(`/hosts/${propertyHostUuid}/structures/`, {
            attribute_values: values,
          })
          .then((r) => r.data)
        await queryClient.invalidateQueries({
          queryKey: ['host-structures', propertyHostUuid],
        })
        setCreatedStructureUuid(created?.uuid ?? null)
        if (created?.uuid) {
          setSessionCreatedUuids((prev) =>
            prev.includes(created.uuid) ? prev : [...prev, created.uuid],
          )
        }
        applyStructureValues(created?.attribute_values ?? values)
        toast.success(
          t('structurePicker.toastAdded', { defaultValue: 'Structure added' }),
        )
        setCreateOpen(false)
      } catch (err) {
        toastApiError(
          err,
          t('structurePicker.toastFailedAdd', {
            defaultValue: 'Failed to add structure',
          }),
        )
        throw err
      } finally {
        setIsCreating(false)
      }
    },
    [applyStructureValues, propertyHostUuid, queryClient, t],
  )

  // Prefill the edit dialog with the target structure's current
  // structure-owned values only.
  const editInitialValues = useMemo(() => {
    const source = editStructure?.attribute_values ?? {}
    const values: Record<string, unknown> = {}
    for (const code of structureCodeNames) {
      if (code in source) values[code] = source[code]
    }
    return values
  }, [editStructure, structureCodeNames])

  const handleUpdate = useCallback(
    async (values: Record<string, unknown>) => {
      if (!propertyHostUuid || !editStructure) return
      setIsEditing(true)
      try {
        const updated = await api
          .put<StructureSummary>(
            `/hosts/${propertyHostUuid}/structures/${editStructure.uuid}/`,
            { attribute_values: values },
          )
          .then((r) => r.data)
        await queryClient.invalidateQueries({
          queryKey: ['host-structures', propertyHostUuid],
        })
        // Keep the wizard's carried host values in sync when the structure
        // being edited is the one currently selected for this registration.
        if (selectedStructureUuid === editStructure.uuid) {
          applyStructureValues(updated?.attribute_values ?? values)
        }
        toast.success(
          t('structurePicker.toastUpdated', {
            defaultValue: 'Structure updated',
          }),
        )
        setEditStructure(null)
      } catch (err) {
        toastApiError(
          err,
          t('structurePicker.toastFailedUpdate', {
            defaultValue: 'Failed to update structure',
          }),
        )
        throw err
      } finally {
        setIsEditing(false)
      }
    },
    [
      applyStructureValues,
      editStructure,
      propertyHostUuid,
      queryClient,
      selectedStructureUuid,
      t,
    ],
  )

  const handleDeleteConfirm = useCallback(async () => {
    if (!propertyHostUuid || !deleteStructure) return
    const removedUuid = deleteStructure.uuid
    setIsDeleting(true)
    try {
      await api.delete(
        `/hosts/${propertyHostUuid}/structures/${removedUuid}/`,
      )
      await queryClient.invalidateQueries({
        queryKey: ['host-structures', propertyHostUuid],
      })
      setSessionCreatedUuids((prev) => prev.filter((u) => u !== removedUuid))
      // If the removed structure was the active selection, clear the carried
      // host values so the wizard does not re-materialise it on completion
      // (get_or_create_structure_from_registration identity-matches those
      // values). Also drop the "created" highlight if it pointed here.
      if (selectedStructureUuid === removedUuid) {
        applyStructureValues({})
      }
      if (createdStructureUuid === removedUuid) {
        setCreatedStructureUuid(null)
      }
      toast.success(
        t('structurePicker.toastRemoved', { defaultValue: 'Structure removed' }),
      )
      setDeleteStructure(null)
    } catch (err) {
      toastApiError(
        err,
        t('structurePicker.toastFailedRemove', {
          defaultValue: 'Failed to remove structure',
        }),
      )
    } finally {
      setIsDeleting(false)
    }
  }, [
    applyStructureValues,
    createdStructureUuid,
    deleteStructure,
    propertyHostUuid,
    queryClient,
    selectedStructureUuid,
    t,
  ])

  // The picker takes control of the structure decision once the chosen
  // property exists, has a structure schema, the operator can view it,
  // and it actually has at least one structure to pick. While in control
  // the host step hides + stops requiring the standalone structure_type /
  // other_structure_type fields (selecting here IS that decision).
  const pickerInControl =
    isExistingProperty &&
    !!structureEntity &&
    canViewStructures &&
    !isLoading &&
    structures.length > 0

  // Report control + owned code_names up to the host step. Memoised by
  // primitive identity so the parent effect doesn't loop.
  const ownedCodeNamesKey = structureCodeNames.join(',')
  useEffect(() => {
    onActivationChange?.({
      active: pickerInControl,
      ownedCodeNames: pickerInControl ? ownedCodeNamesKey.split(',').filter(Boolean) : [],
    })
  }, [pickerInControl, ownedCodeNamesKey, onActivationChange])

  // Speed: when the property has exactly one structure and nothing is
  // selected yet, auto-select it so the operator can continue without an
  // extra click. They can still switch or add a new structure.
  const autoSelectedRef = useRef(false)
  useEffect(() => {
    if (autoSelectedRef.current) return
    if (!pickerInControl) return
    if (structures.length !== 1) return
    if (selectedStructureUuid) return
    // Never auto-default to the catch-all "not listed" fallback - force a
    // deliberate choice instead.
    if (isUnspecifiedStructure(structures[0])) return
    autoSelectedRef.current = true
    applyStructureValues(structures[0].attribute_values ?? {})
  }, [pickerInControl, structures, selectedStructureUuid, applyStructureValues])

  // When the registration was prefilled from the property's stored
  // structure and that stored structure is the catch-all "not listed"
  // fallback, open with nothing selected so the operator makes a
  // deliberate choice (they can still pick the fallback on purpose). Runs
  // once on load; later explicit picks are left untouched.
  const fallbackClearedRef = useRef(false)
  useEffect(() => {
    if (fallbackClearedRef.current) return
    if (!pickerInControl) return
    if (structures.length === 0) return
    fallbackClearedRef.current = true
    if (!selectedStructureUuid || createdStructureUuid) return
    const selected = structures.find((s) => s.uuid === selectedStructureUuid)
    if (selected && isUnspecifiedStructure(selected)) {
      applyStructureValues({})
    }
  }, [
    pickerInControl,
    structures,
    selectedStructureUuid,
    createdStructureUuid,
    applyStructureValues,
  ])

  // Nothing to show for a brand-new property, when the tenant has no
  // structure entity, or when the operator cannot view structures.
  if (!isExistingProperty || !structureEntity || !canViewStructures) {
    return null
  }

  const structureLabel = structureEntity.label || 'Structure'

  return (
    <RegistrationPanel
      title={`Select a ${structureLabel}`}
      icon={<Boxes className="h-3.5 w-3.5 text-[var(--ink-3)]" />}
      bodyClassName="gap-2"
    >
      <p className="m-0 text-[11px] font-light leading-[1.5] text-[var(--ink-3)]">
        Pick the {structureLabel.toLowerCase()} this product is installed on so
        future claims stay accurate, or add a new one.
      </p>
      {isLoading ? (
        <p className="py-2 text-[12px] text-[var(--ink-3)]">
          Looking up {structureLabel.toLowerCase()}s...
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {orderedStructures.map((structure) => {
            const display = structureDisplay(structure)
            const typeValue = String(
              structure.attribute_values?.structure_type ?? '',
            )
            // Config-supplied icon for this type; otherwise give the
            // unspecified "not listed" fallback a distinct default so it
            // never looks like a real structure, and let everything else
            // fall back to the generic Boxes glyph.
            const iconName =
              structureTypeIconByValue[typeValue] ??
              (isUnspecifiedStructure(structure) ? 'circle-dashed' : null)
            // Edit / delete are offered only for structures added inline
            // during this registration session - a property's pre-existing
            // structures are shared data and stay read-only here.
            const isSessionCreated = sessionCreatedUuids.includes(
              structure.uuid,
            )
            return (
              <StructureCard
                key={structure.uuid}
                name={display.name}
                meta={display.meta}
                selected={selectedStructureUuid === structure.uuid}
                onClick={() => handleSelectExisting(structure)}
                iconName={iconName}
                onEdit={
                  isSessionCreated && canEditStructure
                    ? () => setEditStructure(structure)
                    : undefined
                }
                onDelete={
                  isSessionCreated && canDeleteStructure
                    ? () => setDeleteStructure(structure)
                    : undefined
                }
              />
            )
          })}
          {canCreateStructure ? (
            <CreateStructureCard
              selected={!!createdStructureUuid}
              onClick={() => setCreateOpen(true)}
            />
          ) : null}
        </div>
      )}

      {canCreateStructure ? (
        <EditFormDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          title={t('structurePicker.addTitle', {
            label: structureLabel.toLowerCase(),
            defaultValue: 'Add {{label}}',
          })}
          fields={structureFields}
          values={createInitialValues}
          onSave={handleCreate}
          isPending={isCreating}
          submissionMode="submit"
        />
      ) : null}

      {canEditStructure ? (
        <EditFormDialog
          open={!!editStructure}
          onOpenChange={(open) => {
            if (!open) setEditStructure(null)
          }}
          title={t('structurePicker.editTitle', {
            label: structureLabel.toLowerCase(),
            defaultValue: 'Edit {{label}}',
          })}
          fields={structureFields}
          values={editInitialValues}
          onSave={handleUpdate}
          isPending={isEditing}
          submissionMode="submit"
        />
      ) : null}

      {canDeleteStructure ? (
        <ConfirmDeleteDialog
          open={!!deleteStructure}
          onOpenChange={(open) => {
            if (!open) setDeleteStructure(null)
          }}
          title={t('structurePicker.removeTitle', {
            label: structureLabel.toLowerCase(),
            defaultValue: 'Remove {{label}}?',
          })}
          description={t('structurePicker.removeDescription', {
            label: structureLabel.toLowerCase(),
            defaultValue:
              'This {{label}} will be removed from the property and will not be attached to this registration.',
          })}
          confirmLabel={t('structurePicker.removeConfirm', {
            defaultValue: 'Remove',
          })}
          onConfirm={handleDeleteConfirm}
          isPending={isDeleting}
        />
      ) : null}
    </RegistrationPanel>
  )
}
